import React, { useState, useEffect } from 'react';
import { base44 } from "@/api/base44Client";
import { Button } from "@/components/ui/button";
import { Calendar, Clock, Check, X, Loader2, User, Building2 } from "lucide-react";
import { formatChicago } from '@/lib/serverTime';
import { motion } from "framer-motion";

export default function AcceptMeeting() {
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token');

  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [responding, setResponding] = useState(false);
  const [responded, setResponded] = useState(null); // 'accepted' or 'declined'

  useEffect(() => {
    const fetchMeeting = async () => {
      const meetings = await base44.entities.MeetingRequest.filter({ accept_token: token });
      if (meetings.length > 0) {
        setMeeting(meetings[0]);
        if (meetings[0].status !== 'pending') {
          setResponded(meetings[0].status);
        }
      }
      setLoading(false);
    };
    if (token) fetchMeeting();
    else setLoading(false);
  }, [token]);

  const handleResponse = async (status) => {
    setResponding(true);
    await base44.entities.MeetingRequest.update(meeting.id, { status });
    if (status === 'accepted') {
      try {
        // Create Zoom meeting
        const zoomRes = await base44.functions.invoke('createZoomMeeting', {
          firstName: meeting.first_name,
          lastName: meeting.last_name,
          email: meeting.email,
          phone: meeting.phone,
          company: meeting.company,
          title: meeting.title,
          proposedDate: meeting.proposed_date,
          proposedDuration: meeting.proposed_duration,
          repId: meeting.scanned_by || 'adam',
          meetingTopics: meeting.meeting_topics || '',
          internalNotes: meeting.internal_notes || '',
        });
        const zoomMeeting = zoomRes.data;

        // Add to calendar with Zoom link
        const result = await base44.functions.invoke('addToCalendar', {
          data: { ...meeting, status, zoomJoinUrl: zoomMeeting.joinUrl },
        });
        console.log('addToCalendar result:', result.data);
      } catch (err) {
        console.error('Zoom/Calendar error:', err);
      }
    }
    setResponded(status);
    setResponding(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  if (!token || !meeting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
            <X className="w-8 h-8 text-destructive" />
          </div>
          <h2 className="text-xl font-bold text-foreground mb-1">Invalid Link</h2>
          <p className="text-muted-foreground text-sm">This meeting link is not valid or has expired.</p>
        </div>
      </div>
    );
  }

  if (responded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="text-center max-w-sm"
        >
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${responded === 'accepted' ? 'bg-emerald-100' : 'bg-red-100'}`}>
            {responded === 'accepted' ? (
              <Check className="w-8 h-8 text-emerald-600" />
            ) : (
              <X className="w-8 h-8 text-red-600" />
            )}
          </div>
          <h2 className="text-xl font-bold text-foreground mb-1">
            {responded === 'accepted' ? 'Meeting Accepted!' : 'Meeting Declined'}
          </h2>
          <p className="text-muted-foreground text-sm">
            {responded === 'accepted'
              ? `Great! Your meeting with ${meeting.created_by || 'the organizer'} is confirmed.`
              : 'The meeting has been declined.'}
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background safe-top safe-bottom flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm"
      >
        <div className="bg-card rounded-2xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-primary p-6 text-center">
            <div className="w-14 h-14 rounded-full bg-primary-foreground/10 flex items-center justify-center mx-auto mb-3">
              <Calendar className="w-7 h-7 text-primary-foreground" />
            </div>
            <h1 className="text-xl font-bold text-primary-foreground">Meeting Invite</h1>
            <p className="text-sm text-primary-foreground/70 mt-1">You've been invited to connect</p>
          </div>

          {/* Details */}
          <div className="p-6 space-y-4">
            {meeting.proposed_date && (
              <div className="flex items-center gap-3 text-sm">
                <div className="w-9 h-9 rounded-lg bg-accent/10 flex items-center justify-center shrink-0">
                  <Calendar className="w-4.5 h-4.5 text-accent" />
                </div>
                <div>
                  <p className="font-medium text-foreground">{formatChicago(meeting.proposed_date, "EEEE, MMMM d, yyyy")}</p>
                  <p className="text-muted-foreground">{formatChicago(meeting.proposed_date, "h:mm a zzz")}</p>
                </div>
              </div>
            )}

            {meeting.proposed_duration && (
              <div className="flex items-center gap-3 text-sm">
                <div className="w-9 h-9 rounded-lg bg-accent/10 flex items-center justify-center shrink-0">
                  <Clock className="w-4.5 h-4.5 text-accent" />
                </div>
                <p className="font-medium text-foreground">{meeting.proposed_duration} minutes</p>
              </div>
            )}

          </div>

          {/* Actions */}
          <div className="p-6 pt-0 space-y-3">
            <Button
              onClick={() => handleResponse('accepted')}
              disabled={responding}
              className="w-full h-12 text-base font-semibold bg-accent hover:bg-accent/90 text-accent-foreground rounded-xl"
            >
              {responding ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                <>
                  <Check className="w-5 h-5 mr-2" />
                  Accept Meeting
                </>
              )}
            </Button>
            <Button
              onClick={() => handleResponse('declined')}
              disabled={responding}
              variant="outline"
              className="w-full h-12 text-base rounded-xl"
            >
              <X className="w-5 h-5 mr-2" />
              Decline
            </Button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}